import {AutoSave, ChipsOption, Form, FormField} from 'platform/components';
import {Box, Heading, Link, Show, Stack, Text, VStack} from 'platform/foundation';

import {Trans} from 'react-i18next';

import {isEmpty} from 'ramda';

import {useReadCodelistQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {useCurrencies, useTenant} from '@dms/shared';

import {composePath} from 'shared/web';

import {useInterestData} from '../../../../hooks/useInterestData';
import type {ExternalFilter} from '../../types/ExternalFilter';

interface PaymentFormProps {
  interestId: string;
  isDisabled?: boolean;
  onMinipurchaseUpdate: (values: ExternalFilter) => void;
}

export function PaymentForm(props: PaymentFormProps) {
  const {miniPurchase} = useInterestData(props.interestId);
  const {tenantCurrency} = useTenant();
  const {getCurrencySymbol} = useCurrencies();

  const currencySymbol = getCurrencySymbol(tenantCurrency);

  const {data: paymentPreferenceCodelist} = useReadCodelistQuery({
    codelistId: 'payment_preference',
  });

  const paymentPreferenceOptions: ChipsOption[] =
    paymentPreferenceCodelist?.codes
      ?.filter((code) => !code.isDisabled)
      .map((code) => ({
        label: code.name,
        value: code.codeId,
      })) ?? [];

  return (
    <>
      <Heading size={4}>{i18n.t('entity.interest.labels.payment')}</Heading>

      <Form<ExternalFilter>
        isDisabled={props.isDisabled}
        defaultValues={{
          preferredDownPayment: miniPurchase?.preferredDownPayment?.amount
            ? Number(miniPurchase.preferredDownPayment.amount)
            : null,
          paymentPreferenceCodeId: miniPurchase?.paymentPreferenceCodeId
            ? [miniPurchase.paymentPreferenceCodeId]
            : null,
        }}
      >
        {(control, formApi) => (
          <>
            <AutoSave<ExternalFilter>
              onSave={(values) => {
                props.onMinipurchaseUpdate(values);
                // Reset the form dirty baseline to current values after each save.
                // AutoSave only fires when isDirty is true, which react-hook-form sets to false
                // when the field value returns to its original defaultValues. Without this reset,
                // toggling a chips field (e.g. yes → no → yes) would silently skip the second save.
                formApi.reset(formApi.getValues());
              }}
            />
            <Stack direction={['column', 'row']} spacing={4} align="flex-start">
              <Box width={64}>
                <FormField
                  label={i18n.t('entity.interest.labels.preferredDownPayment')}
                  name="preferredDownPayment"
                  type="number"
                  suffix={currencySymbol}
                  control={control}
                />
              </Box>
              <VStack spacing={2}>
                <FormField
                  label={i18n.t('entity.interest.labels.paymentPreference')}
                  name="paymentPreferenceCodeId"
                  type="chips"
                  options={paymentPreferenceOptions}
                  isDeselectable
                  control={control}
                />
                <Show when={isEmpty(paymentPreferenceOptions)}>
                  <Text size="xSmall" color="tertiary">
                    <Trans
                      i18nKey="entity.interest.labels.paymentPreferenceEmptyWithLink"
                      components={{
                        paymentPreferenceLink: (
                          <Link
                            size="xSmall"
                            href={composePath(settingsRoutes.codelistDetail, {
                              params: {id: 'payment_preference'},
                            })}
                            target="_blank"
                          />
                        ),
                      }}
                    />
                  </Text>
                </Show>
              </VStack>
            </Stack>
          </>
        )}
      </Form>
    </>
  );
}
